import { clsx } from 'clsx';
import type { MemberFragment } from '../GroupListing/GroupList.api';
import { UserRole } from '@/apolloClient.types';
import Avatar from '@/components/common/Avatar';
import { renderAvatarName } from '@/utils/common';

interface MemberCardProps {
    member: MemberFragment;
    getRoleLabel: (role: UserRole) => string;
    isLast?: boolean;
}

const getRoleBadgeStyles = (role: UserRole) => {
    switch (role) {
        case UserRole.Trainee:
            return 'bg-neutral-50 text-gray-600 outline-gray-500/10';
        case UserRole.Trainer:
        case UserRole.CompanyAdmin:
        case UserRole.CourseAdmin:
        default:
            // Course Trainer, Admin, Super Admin
            return 'bg-green-50 text-green-700 outline-green-600/20';
    }
};

const MemberCard = ({ member, getRoleLabel, isLast = false }: MemberCardProps) => {
    const firstName = member.firstName || '';
    const lastName = member.lastName || '';
    const initials = renderAvatarName(firstName, lastName);
    const fullName = `${firstName} ${lastName}`.trim();

    return (
        <div className={clsx('flex items-start gap-2.5 p-5', !isLast && 'border-b-[1px] border-neutral-300')}>
            {/* Avatar */}
            <Avatar initials={initials} square={true} className="size-10 flex-shrink-0 bg-teal-100 text-teal-500" />

            {/* Name and Email */}
            <div className="flex flex-1 flex-col justify-center gap-1">
                <div className="leading-6 font-semibold text-neutral-900">{fullName}</div>
                <div className="leading-6 font-medium text-neutral-500">{member.email}</div>
            </div>

            {/* Role Badge */}
            {member.__typename === 'TenantUser' && member.role && (
                <div
                    className={`flex items-center rounded-md px-2 py-0.5 outline-1 outline-offset-[-1px] ${getRoleBadgeStyles(member.role)}`}
                >
                    <div className="text-center text-sm leading-5 font-medium">{getRoleLabel(member.role)}</div>
                </div>
            )}
        </div>
    );
};

export default MemberCard;
